import { Box, styled, useTheme } from '@mui/material'
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFns'
import type { DateRangePickerProps } from '@mui/x-date-pickers-pro/DateRangePicker'
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker'
import { LocalizationProvider } from '@mui/x-date-pickers-pro/LocalizationProvider'
import { MultiInputDateRangeField } from '@mui/x-date-pickers-pro/MultiInputDateRangeField'
import { useFormikContext } from 'formik'

const GroupedInputs = styled(Box)(() => {
  const theme = useTheme()
  return {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',

    fieldset: {
      borderRadius: 0,
      marginLeft: -1,
    },
    '> .MuiFormControl-root': {
      width: '150px',
    },
    '> .MuiMultiInputDateRangeField-separator': {
      width: 0,
    },

    '> *:first-of-type fieldset': {
      borderTopLeftRadius: theme.shape.borderRadius,
      borderBottomLeftRadius: theme.shape.borderRadius,
      marginLeft: 0,
      width: '120px',
    },
    '> *:last-child fieldset': {
      borderTopRightRadius: theme.shape.borderRadius,
      borderBottomRightRadius: theme.shape.borderRadius,
      borderLeftWidth: '0px',
      width: '120px',
    },
  }
}) as typeof Box

interface DatePickerFieldProps extends Partial<DateRangePickerProps> {
  name: string
  startRangeFieldName: string
  endRangeFieldName: string
}

export const DateRangePickerField = ({
  name,
  startRangeFieldName,
  endRangeFieldName,
  ...dateRangePickerProps
}: DatePickerFieldProps) => {
  const { getFieldMeta, setFieldValue } = useFormikContext()
  const parentField = getFieldMeta<{ startDate: Date | null; endDate: Date | null }>(name)

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <DateRangePicker
        format="MM/dd/yy"
        onChange={range => {
          const [startValue, endValue] = range
          setFieldValue(startRangeFieldName, startValue)
          setFieldValue(endRangeFieldName, endValue)
        }}
        value={[
          parentField.value.startDate && new Date(parentField.value.startDate),
          parentField.value.endDate && new Date(parentField.value.endDate),
        ]}
        slots={{ field: MultiInputDateRangeField }}
        slotProps={{
          field: {
            dateSeparator: '',
            style: { width: '240px' },
            // @ts-ignore this prop does in fact exist on this object
            'data-testid': name,
            slots: { root: GroupedInputs },
          },
          textField: {
            size: 'small',
            hiddenLabel: true,
            style: { marginLeft: 0 },
            // @ts-ignore this prop does in fact exist on this object
            'data-testid': 'date-range-input',
          },
        }}
        {...dateRangePickerProps}
      />
    </LocalizationProvider>
  )
}
